import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  officeSaveQuote, officeListCustomers, officeGetRequest,
} from "@/lib/office.functions";
import { PAYMENT_PLANS } from "@/lib/payment-plans";
import { Trash2, Plus, Save, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/_employee/medewerker/office/offertes/nieuw")({
  head: () => ({
    meta: [{ title: "Nieuwe offerte — EVG Office" }, { name: "robots", content: "noindex, nofollow" }],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/medewerker/office/offertes/nieuw" }],
  }),
  validateSearch: (s: Record<string, unknown>): { requestId?: string } => ({
    requestId: typeof s.requestId === "string" ? s.requestId : undefined,
  }),
  component: NewQuote,
});

type LI = { description: string; qty: number; unit_price_cents: number; discount_pct: number };

function fmt(c: number) {
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(c / 100);
}

function NewQuote() {
  const nav = useNavigate();
  const { requestId } = useSearch({ from: "/_employee/medewerker/office/offertes/nieuw" });
  const listCust = useServerFn(officeListCustomers);
  const getReq = useServerFn(officeGetRequest);
  const save = useServerFn(officeSaveQuote);

  const { data: custData } = useQuery({ queryKey: ["office-customers"], queryFn: () => listCust() });
  const { data: reqData } = useQuery({
    queryKey: ["office-request", requestId],
    queryFn: () => getReq({ data: { id: requestId! } }),
    enabled: !!requestId,
  });

  const [userId, setUserId] = useState<string>("");
  const [title, setTitle] = useState("Offerte installatie");
  const [items, setItems] = useState<LI[]>([{ description: "Installatiepakket", qty: 1, unit_price_cents: 50000, discount_pct: 0 }]);
  const [discountPct, setDiscountPct] = useState(0);
  const [plan, setPlan] = useState<keyof typeof PAYMENT_PLANS>("30-50-20");
  const [installDate, setInstallDate] = useState<string>("");
  const [validUntil, setValidUntil] = useState<string>("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (reqData?.request) {
      setTitle(`Offerte ${reqData.request.service} – ${reqData.request.name}`);
      setNotes(reqData.request.message ?? "");
    }
  }, [reqData]);

  const totals = useMemo(() => {
    let sub = 0;
    for (const it of items) {
      sub += Math.round(it.qty * it.unit_price_cents * (1 - it.discount_pct / 100));
    }
    const afterGlobal = Math.round(sub * (1 - discountPct / 100));
    const vat = Math.round(afterGlobal * 0.21);
    return { sub: afterGlobal, vat, total: afterGlobal + vat };
  }, [items, discountPct]);

  const saveMut = useMutation({
    mutationFn: (asSent: boolean) => save({
      data: {
        user_id: userId,
        quote_request_id: requestId ?? null,
        title,
        line_items: items,
        discount_pct: discountPct,
        payment_plan: plan,
        installation_date: installDate || null,
        valid_until: validUntil || null,
        notes,
        status: asSent ? "sent" : "draft",
      },
    }),
    onSuccess: (res) => {
      toast.success("Offerte opgeslagen");
      nav({ to: "/medewerker/office/offertes/$id", params: { id: res.quote.id } });
    },
    onError: (e: any) => toast.error(e.message ?? "Fout bij opslaan"),
  });

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold text-slate-900">Nieuwe offerte</h1>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200 space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Klant</label>
              <select value={userId} onChange={(e) => setUserId(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm">
                <option value="">— selecteer —</option>
                {(custData?.customers ?? []).map((c: any) => (
                  <option key={c.id} value={c.id}>{c.full_name ?? c.email} ({c.email})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Titel</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm" />
            </div>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <h2 className="font-semibold text-slate-900">Regels</h2>
            <div className="mt-3 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input value={it.description} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} placeholder="Omschrijving" className="col-span-5 rounded border border-slate-200 p-2 text-sm" />
                  <input type="number" step="0.5" value={it.qty} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, qty: Number(e.target.value) } : x))} className="col-span-1 rounded border border-slate-200 p-2 text-sm" />
                  <input type="number" step="0.01" value={it.unit_price_cents / 100} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, unit_price_cents: Math.round(Number(e.target.value) * 100) } : x))} className="col-span-2 rounded border border-slate-200 p-2 text-sm" />
                  <input type="number" value={it.discount_pct} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, discount_pct: Number(e.target.value) } : x))} className="col-span-1 rounded border border-slate-200 p-2 text-sm" title="Korting %" />
                  <div className="col-span-2 text-right text-sm font-medium">{fmt(Math.round(it.qty * it.unit_price_cents * (1 - it.discount_pct / 100)))}</div>
                  <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="col-span-1 text-slate-400 hover:text-red-500"><Trash2 className="size-4" /></button>
                </div>
              ))}
            </div>
            <button onClick={() => setItems([...items, { description: "", qty: 1, unit_price_cents: 0, discount_pct: 0 }])} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0b2a4a] hover:underline">
              <Plus className="size-3" /> Regel toevoegen
            </button>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <label className="text-xs font-medium text-slate-500">Notities voor de klant</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm" />
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <h3 className="font-semibold">Instellingen</h3>
            <div className="mt-3 space-y-3 text-sm">
              <div>
                <label className="text-xs text-slate-500">Betaalplan</label>
                <select value={plan} onChange={(e) => setPlan(e.target.value as any)} className="mt-1 w-full rounded border border-slate-200 p-2 text-sm">
                  {Object.entries(PAYMENT_PLANS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">{PAYMENT_PLANS[plan].description}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Extra korting (%)</label>
                <input type="number" value={discountPct} onChange={(e) => setDiscountPct(Number(e.target.value))} className="mt-1 w-full rounded border border-slate-200 p-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Installatiedatum</label>
                <input type="date" value={installDate} onChange={(e) => setInstallDate(e.target.value)} className="mt-1 w-full rounded border border-slate-200 p-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Geldig tot</label>
                <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="mt-1 w-full rounded border border-slate-200 p-2 text-sm" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <h3 className="font-semibold">Totalen</h3>
            <dl className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Subtotaal</dt><dd>{fmt(totals.sub)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">BTW 21%</dt><dd>{fmt(totals.vat)}</dd></div>
              <div className="flex justify-between border-t pt-2 font-semibold"><dt>Totaal</dt><dd>{fmt(totals.total)}</dd></div>
            </dl>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" disabled={!userId || saveMut.isPending} onClick={() => saveMut.mutate(false)}>
              <Save className="size-4 mr-1" /> Opslaan
            </Button>
            <Button disabled={!userId || saveMut.isPending} onClick={() => saveMut.mutate(true)} className="bg-[#0b2a4a] hover:bg-[#0e3463]">
              <Send className="size-4 mr-1" /> Verzenden
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
